import { api } from './api';
import {
  Trip,
  TripDetail,
  TripCoordinate,
  CreateTripPayload,
  CreateTripFromIdeaBody,
  UpdateTripPayload,
  DestinationCountry,
  TripsQueryParams,
  TripTravelBuddiesResponse,
  GoNowReminder,
  RecommendLeaveTimePayload,
  RecommendLeaveTimeResponse,
  SetReminderPayload,
  SetReminderResponse,
  DestinationCountryDetails,
  MergeTripPreviewResponse,
} from '../../types/trip.types';
import {
  ActivityType,
  ActivityDataUnion,
  FlightDelayPrediction,
  TravelContactResponse,
} from '../../types/activity.types';
import {
  ChatMessage,
  SendMessagePayload,
  TripPlannerSendPayload,
  TripPlannerApiResponse,
  AITripPlanningCreateSessionPayload,
  AITripPlanningFinalizePayload,
  AITripPlanningSessionResponse,
  AITripPlanningSubmitAnswersPayload,
} from '../../types/aiChat.types';
import type {
  FlightSearchResult,
  FlightToIdeaPayload,
  FlightToTripResponse,
  TravelClass,
} from '../../types/flight.types';
import type { PlaceToIdeaPayload, PlaceToTripResponse } from '../../types/place.types';

export interface CreateTripStopPayload {
  tripId: string;
  activityType: ActivityType;
  activityData: ActivityDataUnion;
}

export interface TripStopResponse {
  id: number;
  stop_type: ActivityType;
  trip: number;
  amount_spend: number | null;
  belonged_travel_contact?: TravelContactResponse | null;
  link?: string;
  email?: string;
  phone_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export interface TripStopTypesResponse {
  types: ActivityType[];
}

export interface TripStopCoordinate {
  name: string;
  latitude: number;
  longitude: number;
  stopId?: number;
  pinRole?: 'only' | 'first' | 'last' | 'middle';
}

export interface TripStopCoordinatesResponse {
  id: number;
  address: TripStopCoordinate[];
}

export interface Airport {
  name: string;
  iata_code: string;
}

export interface Airline {
  name: string;
  iata_code: string;
}

export interface CreateTravelContactPayload {
  name: string;
  link?: string;
  email?: string;
  phone_number?: string;
}

export interface UpdateTravelContactPayload extends CreateTravelContactPayload {
}

export interface GeneratePackingListRegeneratePayload {
  mode: 'replace_all';
}

export interface PackingListItem {
  id: number;
  category: string;
  title: string;
  is_checked: boolean;
  sort_order: number;
  source?: string;
  locked_by_user?: boolean;
}

export interface PackingListResponse {
  id: number;
  trip?: number | null;
  trip_stop?: number | null;
  created_at: string;
  updated_at: string;
  items: unknown;
}

export interface CreatePackingListItemPayload {
  category: string;
  title: string;
}

export interface UpdatePackingListItemPayload {
  is_checked?: boolean;
  title?: string;
  sort_order?: number;
  locked_by_user?: boolean;
}

export const tripsApi = api.injectEndpoints({
  endpoints: builder => ({
    getTrips: builder.query<Trip[], TripsQueryParams>({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.ordering) queryParams.ordering = params.ordering;
        if (params.start_date) queryParams.start_date = params.start_date;
        if (params.start_date__gte) queryParams.start_date__gte = params.start_date__gte;
        if (params.start_date__lte) queryParams.start_date__lte = params.start_date__lte;

        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trips/`,
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      providesTags: ['Trip'],
      keepUnusedDataFor: 5,
    }),
    getTrip: builder.query<TripDetail, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trips/${id}/`,
        };
      },
      providesTags: (result, error, id) => [{ type: 'Trip', id }],
    }),
    getTripCoordinates: builder.query<TripCoordinate[], TripsQueryParams>({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.ordering) queryParams.ordering = params.ordering;

        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trip-coordinates`,
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      keepUnusedDataFor: 5,
    }),
    getTripStopCoordinates: builder.query<TripStopCoordinatesResponse, string>({
      query: (tripId: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/trip-stop-coordinates/`,
        };
      },
      providesTags: (result, error, tripId) => [{ type: 'Trip', id: tripId }],
      keepUnusedDataFor: 60,
    }),
    getTripStopTypes: builder.query<TripStopTypesResponse, void>({
      query: () => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trip-stop-type`,
        };
      },
      keepUnusedDataFor: 300, 
    }),
    createTrip: builder.mutation<Trip, CreateTripPayload>({
      query: tripData => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trips/`,
          method: 'POST',
          body: tripData,
        };
      },
      invalidatesTags: ['Trip'],
    }),
    createTripFromIdea: builder.mutation<Trip, { ideaId: number; body: CreateTripFromIdeaBody }>({
      query: ({ ideaId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${ideaId}/create-trip/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Trip'],
    }),
    mapIdeaStopToTripStop: builder.mutation<
      TripStopResponse,
      { tripId: number; ideaStopId: number; body: Record<string, unknown> }
    >({
      query: ({ tripId, ideaStopId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/idea-stops/${ideaStopId}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: String(arg.tripId) }],
    }),
    uploadTripCustomCover: builder.mutation<Trip | void, { tripId: number; file: File }>({
      query: ({ tripId, file }) => {
        const formData = new FormData();
        formData.append('image', file);

        return {
          url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/custom-cover/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: arg.tripId.toString() }],
    }),
    deleteTripCustomCover: builder.mutation<void, number>({
      query: tripId => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/custom-cover/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, tripId) => ['Trip', { type: 'Trip', id: tripId.toString() }],
    }),
    updateTrip: builder.mutation<Trip, UpdateTripPayload>({
      query: ({ id, ...tripData }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trips/${id}/`,
          method: 'PATCH',
          body: tripData,
        };
      },
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: arg.id.toString() }],
    }),
    deleteTrip: builder.mutation<void, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/trips/${id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Trip'],
    }),
    saveFlightToTrip: builder.mutation<FlightToTripResponse, { tripId: number; body: FlightToIdeaPayload }>({
      query: ({ tripId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/flight-to-trip`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: String(arg.tripId) }],
    }),
    savePlaceToTrip: builder.mutation<PlaceToTripResponse, { tripId: number; body: PlaceToIdeaPayload }>({
      query: ({ tripId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/place-to-trip`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: String(arg.tripId) }],
    }),
    getDestinationCountries: builder.query<DestinationCountry[], { ordering?: string }>({
      query: (params = {}) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/destination-country`,
          params: params.ordering ? { ordering: params.ordering } : undefined,
        };
      },
      keepUnusedDataFor: 5,
    }),
    createTripStop: builder.mutation<TripStopResponse, CreateTripStopPayload>({
      query: ({ tripId, activityType, activityData }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/trip-stop/`,
          method: 'POST',
          params: { type: activityType },
          body: activityData,
        };
      },
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: arg.tripId }],
    }),

    getTripStop: builder.query<TripStopResponse, number>({
      query: (id: number) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Trip', id: `stop-${id}` }],
      keepUnusedDataFor: 60,
    }),

    updateTripStop: builder.mutation<
      TripStopResponse,
      {
        id: number;
        tripId: string;
        activityType: ActivityType;
        data: Partial<ActivityDataUnion>;
      }
    >({
      query: ({ id, activityType, data }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${id}`,
        method: 'PATCH',
        params: { type: activityType },
        body: data,
      }),
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: arg.tripId }],
    }),

    deleteTripStop: builder.mutation<void, { id: number; tripId: string }>({
      query: ({ id }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => ['Trip', { type: 'Trip', id: arg.tripId }],
    }),

    getAirports: builder.query<Airport[], { search?: string; type?: string }>({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.search) queryParams.search = params.search;
        if (params.type) queryParams.type = params.type;

        return {
          url: `${process.env.REACT_APP_API_URL}/trips/airports`,
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      keepUnusedDataFor: 60,
    }),

    getAirlines: builder.query<Airline[], { search?: string }>({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.search) queryParams.search = params.search;

        return {
          url: `${process.env.REACT_APP_API_URL}/trips/airlines`,
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      keepUnusedDataFor: 60,
    }),

    previewMergeTrips: builder.mutation<
      MergeTripPreviewResponse,
      { tripId: number; mergeTrips: number[] }
    >({
      query: ({ tripId, mergeTrips }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/merge/preview/`,
        method: 'POST',
        body: { merge_trips: mergeTrips },
      }),
    }),

    mergeTrips: builder.mutation<
      void,
      { tripId: number; mergeTrips: number[]; discard_stop_ids?: number[] }
    >({
      query: ({ tripId, mergeTrips, discard_stop_ids }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/merge/`,
        method: 'PUT',
        body: {
          merge_trips: mergeTrips,
          ...(discard_stop_ids?.length ? { discard_stop_ids } : {}),
        },
      }),
      invalidatesTags: ['Trip'],
    }),

    getTripTravelBuddies: builder.query<TripTravelBuddiesResponse, string>({
      query: (tripId: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/travel-buddies`,
        };
      },
      keepUnusedDataFor: 60,
    }),

    getFlightDelayPrediction: builder.query<FlightDelayPrediction[], number>({
      query: (activityId: number) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/flight-delay-predictions/${activityId}`,
        };
      },
      keepUnusedDataFor: 60,
    }),

    sendChatMessage: builder.mutation<ChatMessage, { tripId: string; payload: SendMessagePayload }>({
      query: ({ tripId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trips/${tripId}/chat`,
        method: 'POST',
        body: payload,
      }),
    }),

    getChatHistory: builder.query<ChatMessage[], string>({
      query: (tripId: string) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trips/${tripId}/chat-history`,
      }),
      keepUnusedDataFor: 60,
    }),

    sendTripPlannerMessage: builder.mutation<
      TripPlannerApiResponse,
      { tripId: string; payload: TripPlannerSendPayload }
    >({
      query: ({ tripId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trips/${tripId}/chat/trip-planer`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, { tripId }) => [{ type: 'Trip', id: tripId }],
    }),

    createAITripPlanningSession: builder.mutation<
      AITripPlanningSessionResponse,
      AITripPlanningCreateSessionPayload
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trip-planning/sessions/`,
        method: 'POST',
        body: payload,
      }),
    }),

    getAITripPlanningSession: builder.query<AITripPlanningSessionResponse, number>({
      query: sessionId => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trip-planning/sessions/${sessionId}/`,
      }),
    }),

    submitAITripPlanningAnswers: builder.mutation<
      AITripPlanningSessionResponse,
      { sessionId: number; payload: AITripPlanningSubmitAnswersPayload }
    >({
      query: ({ sessionId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trip-planning/sessions/${sessionId}/answers/`,
        method: 'POST',
        body: payload,
      }),
    }),

    generateAITripPlanningPreview: builder.mutation<AITripPlanningSessionResponse, { sessionId: number }>({
      query: ({ sessionId }) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trip-planning/sessions/${sessionId}/preview/`,
        method: 'POST',
      }),
    }),

    finalizeAITripPlanningSession: builder.mutation<
      AITripPlanningSessionResponse,
      { sessionId: number; payload: AITripPlanningFinalizePayload }
    >({
      query: ({ sessionId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/ai/trip-planning/sessions/${sessionId}/finalize/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Trip'],
    }),

    getTravelContacts: builder.query<TravelContactResponse[], string>({
      query: (tripStopId: string) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${tripStopId}/travel-contact`,
      }),
      providesTags: (result, error, tripStopId) => [{ type: 'TravelContact', id: tripStopId }, 'TravelContact'],
      keepUnusedDataFor: 60,
    }),

    createTravelContact: builder.mutation<
      TravelContactResponse,
      { tripStopId: string; contactData: CreateTravelContactPayload }
    >({
      query: ({ tripStopId, contactData }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${tripStopId}/travel-contact`,
        method: 'POST',
        body: contactData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'TravelContact', id: arg.tripStopId }, 'TravelContact', 'Trip'],
    }),

    updateTravelContact: builder.mutation<
      TravelContactResponse,
      {
        tripStopId: string;
        contactId: string;
        contactData: UpdateTravelContactPayload;
      }
    >({
      query: ({ tripStopId, contactId, contactData }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${tripStopId}/travel-contacts/${contactId}/`,
        method: 'PUT',
        body: contactData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'TravelContact', id: arg.tripStopId }, 'TravelContact', 'Trip'],
    }),

    getTripStopGoNowReminder: builder.query<GoNowReminder, string>({
      query: (tripStopId: string) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${tripStopId}/go-now-reminder`,
      }),
      providesTags: (result, error, tripStopId) => [{ type: 'GoNowReminder', id: tripStopId }],
      keepUnusedDataFor: 60,
    }),

    getGoNowReminders: builder.query<GoNowReminder[], void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/trips/go-now-reminders/`,
      }),
      providesTags: ['GoNowReminder'],
      keepUnusedDataFor: 60
    }),

    getGoNowReminder: builder.query<GoNowReminder, string>({
      query: (id: string) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/go-now-reminders/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'GoNowReminder', id }],
      keepUnusedDataFor: 60,
    }),

    deleteGoNowReminder: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/go-now-reminders/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => ['GoNowReminder', { type: 'GoNowReminder', id }],
    }),

    recommendLeaveTime: builder.mutation<
      RecommendLeaveTimeResponse,
      { tripStopId: string; payload: RecommendLeaveTimePayload }
    >({
      query: ({ tripStopId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${tripStopId}/recommend-leave-time`,
        method: 'POST',
        body: payload,
      }),
    }),

    setReminder: builder.mutation<SetReminderResponse, { tripStopId: string; payload: SetReminderPayload }>({
      query: ({ tripStopId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${tripStopId}/set-reminder`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => ['GoNowReminder', { type: 'GoNowReminder', id: arg.tripStopId }],
    }),

    getDestinationCountryDetails: builder.query<DestinationCountryDetails, number>({
      query: (countryId: number) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/trips/destination-country/${countryId}/about-country`,
        };
      },
      keepUnusedDataFor: 300,
    }),

    getTripPackingList: builder.query<PackingListResponse, number>({
      query: (tripId: number) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/`,
      }),
      providesTags: (result, error, tripId) => [{ type: 'PackingList', id: `trip-${tripId}` }],
    }),

    generateTripPackingList: builder.mutation<
      PackingListResponse,
      { tripId: number; payload?: GeneratePackingListRegeneratePayload }
    >({
      query: ({ tripId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/generate/`,
        method: 'POST',
        ...(payload ? { body: payload } : {}),
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `trip-${arg.tripId}` }],
    }),

    getTripPackingListItem: builder.query<PackingListItem, { tripId: number; itemId: number }>({
      query: ({ tripId, itemId }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/items/${itemId}/`,
      }),
      providesTags: (result, error, arg) => [{ type: 'PackingList', id: `trip-${arg.tripId}` }],
    }),

    createTripPackingListItem: builder.mutation<
      PackingListItem,
      { tripId: number; payload: CreatePackingListItemPayload }
    >({
      query: ({ tripId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/items/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `trip-${arg.tripId}` }],
    }),

    updateTripPackingListItem: builder.mutation<
      PackingListItem,
      { tripId: number; itemId: number; payload: UpdatePackingListItemPayload }
    >({
      query: ({ tripId, itemId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/items/${itemId}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `trip-${arg.tripId}` }],
    }),

    deleteTripPackingListItem: builder.mutation<void, { tripId: number; itemId: number }>({
      query: ({ tripId, itemId }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/${tripId}/packing-list/items/${itemId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `trip-${arg.tripId}` }],
    }),

    getStopPackingList: builder.query<PackingListResponse, number>({
      query: (stopId: number) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/`,
      }),
      providesTags: (result, error, stopId) => [{ type: 'PackingList', id: `stop-${stopId}` }],
    }),

    generateStopPackingList: builder.mutation<
      PackingListResponse,
      { stopId: number; payload?: GeneratePackingListRegeneratePayload }
    >({
      query: ({ stopId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/generate/`,
        method: 'POST',
        ...(payload ? { body: payload } : {}),
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `stop-${arg.stopId}` }],
    }),

    getStopPackingListItem: builder.query<PackingListItem, { stopId: number; itemId: number }>({
      query: ({ stopId, itemId }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/items/${itemId}/`,
      }),
      providesTags: (result, error, arg) => [{ type: 'PackingList', id: `stop-${arg.stopId}` }],
    }),

    createStopPackingListItem: builder.mutation<
      PackingListItem,
      { stopId: number; payload: CreatePackingListItemPayload }
    >({
      query: ({ stopId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/items/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `stop-${arg.stopId}` }],
    }),

    updateStopPackingListItem: builder.mutation<
      PackingListItem,
      { stopId: number; itemId: number; payload: UpdatePackingListItemPayload }
    >({
      query: ({ stopId, itemId, payload }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/items/${itemId}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `stop-${arg.stopId}` }],
    }),

    deleteStopPackingListItem: builder.mutation<void, { stopId: number; itemId: number }>({
      query: ({ stopId, itemId }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stops/${stopId}/packing-list/items/${itemId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'PackingList', id: `stop-${arg.stopId}` }],
    }),

    getTripStopFlightAlternatives: builder.query<
      FlightSearchResult,
      { stopId: number; travel_class?: TravelClass; travelers_count?: number }
    >({
      query: ({ stopId, travel_class = 'ECONOMY', travelers_count }) => ({
        url: `${process.env.REACT_APP_API_URL}/trips/trip-stop/${stopId}/flight-alternatives`,
      }),
    }),
  }),
});

export const {
  useGetTripsQuery,
  useGetTripQuery,
  useGetTripCoordinatesQuery,
  useGetTripStopCoordinatesQuery,
  useGetTripStopTypesQuery,
  useCreateTripMutation,
  useCreateTripFromIdeaMutation,
  useMapIdeaStopToTripStopMutation,
  useUploadTripCustomCoverMutation,
  useDeleteTripCustomCoverMutation,
  useUpdateTripMutation,
  useDeleteTripMutation,
  useSaveFlightToTripMutation,
  useSavePlaceToTripMutation,
  useGetDestinationCountriesQuery,
  useCreateTripStopMutation,
  useGetTripStopQuery,
  useUpdateTripStopMutation,
  useDeleteTripStopMutation,
  useGetAirportsQuery,
  useGetAirlinesQuery,
  usePreviewMergeTripsMutation,
  useMergeTripsMutation,
  useGetTripTravelBuddiesQuery,
  useGetFlightDelayPredictionQuery,
  useSendChatMessageMutation,
  useGetChatHistoryQuery,
  useSendTripPlannerMessageMutation,
  useCreateAITripPlanningSessionMutation,
  useGetAITripPlanningSessionQuery,
  useSubmitAITripPlanningAnswersMutation,
  useGenerateAITripPlanningPreviewMutation,
  useFinalizeAITripPlanningSessionMutation,
  useGetTravelContactsQuery,
  useCreateTravelContactMutation,
  useUpdateTravelContactMutation,
  useGetTripStopGoNowReminderQuery,
  useGetGoNowRemindersQuery,
  useGetGoNowReminderQuery,
  useDeleteGoNowReminderMutation,
  useRecommendLeaveTimeMutation,
  useSetReminderMutation,
  useGetDestinationCountryDetailsQuery,
  useGetTripPackingListQuery,
  useGenerateTripPackingListMutation,
  useGetTripPackingListItemQuery,
  useCreateTripPackingListItemMutation,
  useUpdateTripPackingListItemMutation,
  useDeleteTripPackingListItemMutation,
  useGetStopPackingListQuery,
  useGenerateStopPackingListMutation,
  useGetStopPackingListItemQuery,
  useCreateStopPackingListItemMutation,
  useUpdateStopPackingListItemMutation,
  useDeleteStopPackingListItemMutation,
  useGetTripStopFlightAlternativesQuery,
} = tripsApi;
